import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Upload, FileText, Database, ArrowRightLeft, Save, GripHorizontal, Check, X, RefreshCw, Play, Minimize2, Eye, ArrowUp, Download, Expand, ChevronDown, ChevronUp, GitMerge, AlertTriangle, ArrowRight, CheckCircle, Plus, Minus, ShieldX } from 'lucide-react';
import { compareFiles, compareText, compareContract, getContracts, createContract, createVersion, saveAndResolve, parseFile, resolveDriftEvent, rejectDriftEvent } from '../api';
import DiffViewer from './DiffViewer';
import SmartSearchBar from './SmartSearchBar';
import Modal from './Modal';

import ExportConfigModal from './ExportConfigModal';
import ExportDropdown from './ExportDropdown';
import { EDI_SAMPLE, JSON_SAMPLE } from './sampleData';
import VendorSelect from './contracts/VendorSelect';

// Stable empty array reference to avoid infinite re-render loops when passed as props
const EMPTY_ARRAY = [];

const Comparator = ({ isAuthenticated = false, isDarkMode, addToast, onContractSaved, refreshTrigger, matchingSettings = { hideThreshold: 30, greyThreshold: 50, highConfidenceThreshold: 80, defaultExportFormat: 'json' }, reviewDriftEvent = null, onReviewComplete, onFullscreenChange, preSelectContractId = null, onPreSelectConsumed, onSignInClick }) => {
    const [sourceMode, setSourceMode] = useState('file');
    const [targetMode, setTargetMode] = useState('file');
    const [appMode, setAppMode] = useState(() => {
        try {
            const saved = localStorage.getItem('driftops_comparator_mode');
            return (saved === 'view' || saved === 'compare') ? saved : 'compare';
        } catch {
            return 'compare';
        }
    }); // 'compare' or 'view'

    const [sourceFile, setSourceFile] = useState(null);
    const [targetFile, setTargetFile] = useState(null);
    const [sourceText, setSourceText] = useState('');
    const [targetText, setTargetText] = useState('');
    const [selectedContractId, setSelectedContractId] = useState('');

    const [sourcePreview, setSourcePreview] = useState('');
    const [targetPreview, setTargetPreview] = useState('');

    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [appliedRenames, setAppliedRenames] = useState(new Set());
    const [modifiedTargetData, setModifiedTargetData] = useState(null);


    const [activeModal, setActiveModal] = useState(null);
    const [contractName, setContractName] = useState('');
    const [contractVendor, setContractVendor] = useState('');
    const [changeSummary, setChangeSummary] = useState('');
    const [saveOption, setSaveOption] = useState('new_contract'); // 'new_version' or 'new_contract'
    const [selectedContractForVersion, setSelectedContractForVersion] = useState(''); // Contract to update when saving as new version
    const [savePreviewOpen, setSavePreviewOpen] = useState(false); // Toggle for save preview diff
    const [hasFailedAutoParse, setHasFailedAutoParse] = useState(false); // Guard against loop if auto-parse fails

    // Drag-and-drop state
    const [isDraggingSource, setIsDraggingSource] = useState(false);
    const [isDraggingTarget, setIsDraggingTarget] = useState(false);

    // EDI-specific frontend stats state
    const [ediStats, setEdiStats] = useState(null);

    // Interactive features state
    const [highlightedField, setHighlightedField] = useState(null);

    const [showBackToTop, setShowBackToTop] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isRenamesCollapsed, setIsRenamesCollapsed] = useState(false);
    const [isTypeChangesCollapsed, setIsTypeChangesCollapsed] = useState(false);
    const [acceptedTypeChanges, setAcceptedTypeChanges] = useState(new Set());
    const [rejectedAdditions, setRejectedAdditions] = useState(new Set());
    const [rejectedRemovals, setRejectedRemovals] = useState(new Set());

    // Smart Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('all');
    const [searchMatches, setSearchMatches] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [changesFilter, setChangesFilter] = useState('all');
    const [showSchemaDriftOnly, setShowSchemaDriftOnly] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);

    // Refs for scroll control
    const diffViewerRef = useRef(null);
    const resultsContainerRef = useRef(null);
    const searchInputRef = useRef(null);

    const [resultsHeight, setResultsHeight] = useState(600);
    const [isDragging, setIsDragging] = useState(false);
    const isResizing = useRef(false);

    // Track if we're reviewing a drift event
    const [reviewingEventId, setReviewingEventId] = useState(null);

    const [upsellDismissed, setUpsellDismissed] = useState(
        () => !!sessionStorage.getItem('driftops_upsell_dismissed')
    );

    const dismissUpsell = () => {
        sessionStorage.setItem('driftops_upsell_dismissed', '1');
        setUpsellDismissed(true);
    };

    const loadSample = (sample) => {
        setSourceMode('text');
        setTargetMode('text');
        setSourceText(sample.source);
        setTargetText(sample.target);
        setSourceFile(null);
        setTargetFile(null);
        setSourcePreview('');
        setTargetPreview('');
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchContracts();
        }
    }, [refreshTrigger, isAuthenticated]);

    // Persist appMode to localStorage
    useEffect(() => {
        localStorage.setItem('driftops_comparator_mode', appMode);
    }, [appMode]);

    // Debug log for matchingSettings - disabled to prevent console spam
    // useEffect(() => {
    //     console.log('matchingSettings:', matchingSettings);
    // }, [matchingSettings]);

    // Notify parent when fullscreen changes
    useEffect(() => {
        onFullscreenChange?.(isFullscreen);
    }, [isFullscreen, onFullscreenChange]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            const tag = e.target.tagName;
            const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

            // '?' to toggle keyboard shortcuts panel (when not in an input)
            if (e.key === '?' && !inInput) {
                e.preventDefault();
                setShowShortcuts(prev => !prev);
                return;
            }

            // '/' to focus search (when result exists and not already in an input)
            if (e.key === '/' && !inInput && result) {
                e.preventDefault();
                searchInputRef.current?.focus();
                return;
            }

            // ESC: close shortcuts panel first, then clear search, then toggle fullscreen
            if (e.key === 'Escape') {
                if (showShortcuts) {
                    e.preventDefault();
                    setShowShortcuts(false);
                    return;
                }
                if (result && searchQuery) {
                    e.preventDefault();
                    setSearchQuery('');
                    searchInputRef.current?.blur();
                } else if (result) {
                    e.preventDefault();
                    setIsFullscreen(prev => !prev);
                }
            }
            // Ctrl/Cmd + Enter to trigger comparison / view
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                const compareBtn = document.getElementById('run-compare-btn');
                const viewBtn = document.getElementById('run-view-btn');
                if (compareBtn && !compareBtn.disabled) {
                    e.preventDefault();
                    compareBtn.click();
                } else if (viewBtn && !viewBtn.disabled) {
                    e.preventDefault();
                    viewBtn.click();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [result, searchQuery, setSearchQuery, showShortcuts]);

    // Handle pre-selected contract from navigation
    useEffect(() => {
        if (preSelectContractId && contracts.length > 0) {
            // Switch to contract source mode and select the contract
            setSourceMode('contract');
            setSelectedContractId(String(preSelectContractId));
            // Clear the pre-selection
            onPreSelectConsumed?.();
        }
    }, [preSelectContractId, contracts, onPreSelectConsumed]);

    // Handle when a drift event is passed in for review
    useEffect(() => {
        if (!reviewDriftEvent) return;

        setReviewingEventId(reviewDriftEvent.id || null);
        setAppliedRenames(new Set());
        setIsFullscreen(false);

        const srcContent = reviewDriftEvent.source_content;
        const tgtContent = reviewDriftEvent.target_content;

        const srcStr = JSON.stringify(srcContent, null, 2);
        const tgtStr = JSON.stringify(tgtContent, null, 2);
        setSourceMode('text');
        setSourceText(srcStr);
        setSourcePreview(srcStr);
        setTargetMode('text');
        setTargetText(tgtStr);
        setTargetPreview(tgtStr);

        // Clean the stored diff_report of stale/spurious renames.
        // The current backend tags every valid EDI rename with type = "edi_element_change"
        // or type = "edi_segment_change". Old / stale diffs (generated before this was added)
        // have NLP-matcher false-positives stored without a `type` field, and old EDI renames
        // that used raw element values (e.g. "ZZ", "FIP") as `old_field` instead of the
        // human-readable element name. If ANY typed EDI rename exists, discard all un-typed
        // entries — they are guaranteeably the stale garbage.
        const rawDiff = reviewDriftEvent.diff_report || {};
        const allRenames = rawDiff.suggested_renames || [];
        const EDI_TYPES = new Set(['edi_element_change', 'edi_segment_change']);
        const hasTypedEdiRenames = allRenames.some(r => EDI_TYPES.has(r.type));
        const filteredRenames = hasTypedEdiRenames
            ? allRenames.filter(r => EDI_TYPES.has(r.type))
            : allRenames;
        const isEdiContent = (
            srcContent &&
            typeof srcContent === 'object' &&
            typeof srcContent.detected_format === 'string' &&
            srcContent.detected_format.startsWith('x12')
        );

        // If no typed renames exist but it's an EDI file, all stored renames are stale NLP noise.
        // Show zero cards rather than wrong cards.
        const finalRenames = hasTypedEdiRenames
            ? filteredRenames
            : (isEdiContent ? [] : allRenames);

        const cleanedDiff = {
            ...rawDiff,
            suggested_renames: finalRenames,
        };

        setResult({
            source_data: srcContent,
            target_data: tgtContent,
            diff: cleanedDiff,
        });
        setModifiedTargetData(tgtContent ? JSON.parse(JSON.stringify(tgtContent)) : null);

        addToast(`Loaded drift: ${reviewDriftEvent.source_name} → ${reviewDriftEvent.target_name}`, 'info');
    }, [reviewDriftEvent]);

    const fetchContracts = async () => {
        try {
            const data = await getContracts();
            setContracts(data);
        } catch (error) {
            console.error("Failed to fetch contracts", error);
            addToast("Failed to fetch contracts", "error");
        }
    };

    const autoParseAttempted = useRef(false);

    // Auto-parse when switching to View mode if file is selected but no result shown
    useEffect(() => {
        if (appMode === 'view' && (sourceFile || sourceText) && !autoParseAttempted.current && !loading && !hasFailedAutoParse) {
            autoParseAttempted.current = true;
            handleViewFile();
        }
    }, [appMode, sourceFile, sourceText, loading, hasFailedAutoParse]);

    // Reset auto-parse flag when inputs change or mode changes
    useEffect(() => {
        autoParseAttempted.current = false;
        setHasFailedAutoParse(false);
    }, [sourceFile, sourceText, appMode]);

    const readFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    // Mirrors backend body limit. Updating this without bumping the server
    // middleware will only frustrate users at submit time.
    const MAX_FILE_SIZE_MB = 50;
    const ALLOWED_EXTENSIONS = ['.json', '.xml', '.xsd', '.edi', '.txt', '.csv', '.x12'];

    const validateFile = (file) => {
        if (!file) return 'No file selected';
        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > MAX_FILE_SIZE_MB) {
            return `File too large (${sizeMb.toFixed(1)} MB). Max ${MAX_FILE_SIZE_MB} MB.`;
        }
        const lower = file.name.toLowerCase();
        const ok = ALLOWED_EXTENSIONS.some(ext => lower.endsWith(ext));
        if (!ok) {
            return `Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
        }
        return null;
    };

    const processSourceFile = async (file) => {
        const err = validateFile(file);
        if (err) { addToast(err, 'error'); return; }
        setSourceFile(file);
        try {
            const content = await readFile(file);
            setSourcePreview(content);

            // Auto-parse in View mode
            if (appMode === 'view') {
                setLoading(true);
                try {
                    const data = await parseFile(file);
                    setResult({
                        source_data: data.data,
                        target_data: null,
                        diff: { added: [], removed: [], changed: [], type_changes: [], renames: [] }
                    });
                    setModifiedTargetData(null);
                    setAppliedRenames(new Set());
                } catch (err) {
                    console.error(err);
                    addToast("Parse failed: " + (err.response?.data?.detail || err.message), "error");
                } finally {
                    setLoading(false);
                }
            }
        } catch (err) {
            console.error("Failed to read file", err);
        }
    };

    const handleSourceFileChange = (e) => {
        const file = e.target.files[0];
        if (file) processSourceFile(file);
    };

    const handleDragOverSource = (e) => { e.preventDefault(); setIsDraggingSource(true); };
    const handleDragLeaveSource = (e) => { e.preventDefault(); setIsDraggingSource(false); };
    const handleDropSource = (e) => {
        e.preventDefault();
        setIsDraggingSource(false);
        const file = e.dataTransfer.files[0];
        if (file) processSourceFile(file);
    };

    const processTargetFile = async (file) => {
        const err = validateFile(file);
        if (err) { addToast(err, 'error'); return; }
        setTargetFile(file);
        try {
            const content = await readFile(file);
            setTargetPreview(content);
        } catch (err) {
            console.error("Failed to read file", err);
        }
    };

    const handleTargetFileChange = (e) => {
        const file = e.target.files[0];
        if (file) processTargetFile(file);
    };

    const handleDragOverTarget = (e) => { e.preventDefault(); setIsDraggingTarget(true); };
    const handleDragLeaveTarget = (e) => { e.preventDefault(); setIsDraggingTarget(false); };
    const handleDropTarget = (e) => {
        e.preventDefault();
        setIsDraggingTarget(false);
        const file = e.dataTransfer.files[0];
        if (file) processTargetFile(file);
    };

    const handleCompare = async () => {
        setLoading(true);
        setResult(null);
        setIsFullscreen(false);
        setAppliedRenames(new Set());
        setModifiedTargetData(null);
        try {
            // Build settings object for API (convert to backend format)
            const apiSettings = {
                contextWeight: matchingSettings.contextWeight,
                // Use greyThreshold as the default confidence threshold for API
                confidenceThreshold: matchingSettings.greyThreshold / 100,
            };

            let res;
            if (sourceMode === 'contract') {
                if (!selectedContractId) return addToast("Please select a contract", "error");

                if (targetMode === 'file') {
                    if (!targetFile) return addToast("Please upload a target file", "error");
                    res = await compareContract(selectedContractId, targetFile, apiSettings);
                } else {
                    // targetMode === 'text'
                    if (!targetText) return addToast("Please enter target text", "error");

                    const contract = contracts.find(c => c.id == selectedContractId);
                    if (!contract) return addToast("Selected contract not found", "error");

                    // Use compareText with contract schema as source
                    const src = JSON.stringify(contract.schema_content, null, 2);
                    res = await compareText(src, targetText, apiSettings);
                }
            } else if (sourceMode === 'file' && targetMode === 'file') {
                if (!sourceFile || !targetFile) return addToast("Please upload both files", "error");
                res = await compareFiles(sourceFile, targetFile, apiSettings);
            } else {
                const src = sourceMode === 'file' ? await readFile(sourceFile) : sourceText;
                const tgt = targetMode === 'file' ? await readFile(targetFile) : targetText;
                res = await compareText(src, tgt, apiSettings);
            }
            setEdiStats(null);
            setResult(res);
            addToast(`Found ${res.diff?.suggested_renames?.length || 0} semantic renames, ${res.diff?.type_changes?.length || 0} type changes`, 'info');
            setModifiedTargetData(JSON.parse(JSON.stringify(res.target_data)));
            addToast("Comparison complete!", "success");
        } catch (error) {
            console.error(error);
            addToast("Comparison failed: " + (error.response?.data?.detail || error.message), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleViewFile = async () => {
        if (!sourceFile && !sourceText) {
            addToast("Please upload or paste a file to view", "error");
            return;
        }

        setLoading(true);
        try {
            let data;
            if (sourceFile) {
                data = await parseFile(sourceFile);
            } else {
                // For text input, create a blob and parse
                const blob = new Blob([sourceText], { type: 'text/plain' });
                const file = new File([blob], 'input.txt', { type: 'text/plain' });
                data = await parseFile(file);
            }

            // Set result for viewing (no diff, just parsed data)
            setResult({
                source_data: data.data,
                target_data: null,
                diff: { added: [], removed: [], changed: [], type_changes: [], renames: [] }
            });
            setModifiedTargetData(null);
            setAppliedRenames(new Set());
            addToast("File parsed successfully!", "success");
        } catch (error) {
            console.error(error);
            setHasFailedAutoParse(true);
            addToast("Parse failed: " + (error.response?.data?.detail || error.message), "error");
        } finally {
            setLoading(false);
        }
    };

    const openSaveModal = (type) => {
        // Smart Save: if a contract is selected, default to updating it
        if (selectedContractId) {
            setSaveOption('new_version');
            setSelectedContractForVersion(selectedContractId);
            setContractName('');
        } else {
            // No contract selected — default to creating a new one
            setSaveOption('new_contract');
            setSelectedContractForVersion('');
            setContractName('');
        }
        setContractVendor('');
        setChangeSummary('');
        setActiveModal(type);
    };

    const buildChangeManifest = () => {
        if (!result?.diff) return undefined;

        const renames = result.diff.suggested_renames || [];
        return {
            renames_accepted: Array.from(appliedRenames).map(i => {
                const r = renames[i];
                return r ? { old_field: r.old_field, new_field: r.new_field, confidence: r.confidence } : null;
            }).filter(Boolean),
            renames_rejected: renames
                .map((r, i) => ({ ...r, _idx: i }))
                .filter((_, i) => !appliedRenames.has(i))
                .map(r => ({ old_field: r.old_field, new_field: r.new_field, confidence: r.confidence })),
            additions_rejected: Array.from(rejectedAdditions).map(i => {
                const a = (result.diff.added || [])[i];
                return a ? { path: a.path, value: a.value } : null;
            }).filter(Boolean),
            removals_rejected: Array.from(rejectedRemovals).map(i => {
                const r = (result.diff.removed || [])[i];
                return r ? { path: r.path, value: r.value } : null;
            }).filter(Boolean),
            type_changes_accepted: Array.from(acceptedTypeChanges).map(i => {
                const tc = (result.diff.type_changes || [])[i];
                return tc ? { path: tc.path, old_value: tc.old_value, new_value: tc.new_value } : null;
            }).filter(Boolean),
        };
    };

    const handleSaveContract = async () => {
        const content = activeModal === 'saveSource' ? result?.source_data : modifiedTargetData;

        // Pre-flight validation
        if (!content || typeof content !== 'object') {
            return addToast("Cannot save: schema content is empty or invalid", "error");
        }
        if (!Array.isArray(content) && Object.keys(content).length === 0) {
            return addToast("Cannot save: schema content is an empty object", "error");
        }
        if (Array.isArray(content) && content.length === 0) {
            return addToast("Cannot save: schema content is an empty list", "error");
        }

        try {
            if (saveOption === 'new_contract') {
                if (!contractName) return addToast("Please enter a contract name", "error");
                await createContract({
                    name: contractName,
                    schema_content: content,
                    vendor: contractVendor.trim() || null,
                });
                addToast("Contract saved successfully!", "success");

                // Resolve drift event separately for new contracts (no version to link)
                if (reviewingEventId) {
                    try {
                        await resolveDriftEvent(reviewingEventId, {
                            resolution_notes: 'Saved as new contract from Comparator',
                            actions: { auto_version: false, notify_slack: true }
                        });
                        addToast('Drift event auto-resolved', 'success');
                        setReviewingEventId(null);
                        onReviewComplete?.();
                    } catch (err) {
                        console.error('Auto-resolve failed:', err);
                        addToast('Saved, but failed to auto-resolve drift event', 'warning');
                    }
                }
            } else {
                // Save as new version
                const contractToUpdate = selectedContractForVersion || selectedContractId;
                if (!contractToUpdate) return addToast("No contract selected for version update", "error");

                const changeManifest = buildChangeManifest();

                if (reviewingEventId) {
                    // Atomic save + resolve in one transaction
                    const result = await saveAndResolve(contractToUpdate, {
                        schema_content: content,
                        change_summary: changeSummary || "Manual save via Comparator",
                        changes_accepted: changeManifest,
                        change_type: 'manual',
                        drift_event_id: reviewingEventId,
                        resolution_notes: 'Saved as new version from Comparator',
                    });
                    addToast("New version created & drift resolved!", "success");
                    setReviewingEventId(null);
                    onReviewComplete?.();
                } else {
                    // Normal version save (no drift event)
                    await createVersion(contractToUpdate, {
                        schema_content: content,
                        change_summary: changeSummary || "Manual save via Comparator",
                        changes_accepted: changeManifest,
                        change_type: 'manual'
                    });
                    addToast("New version created successfully!", "success");
                }
            }

            fetchContracts();
            if (onContractSaved) onContractSaved();

            setActiveModal(null);
            setContractName('');
            setContractVendor('');
            setChangeSummary('');
            setSelectedContractForVersion('');

            // In review mode, clear results to signal review is complete
            if (reviewingEventId) {
                setResult(null);
            }
        } catch (error) {
            console.error(error);
            const detail = error.response?.data?.detail || error.message;
            addToast("Failed to save: " + detail, "error");
        }
    };

    const handleRevert = () => {
        setResult(null);
        setActiveModal(null);
        setIsFullscreen(false);
        setAppliedRenames(new Set());
        setModifiedTargetData(null);
        // Clear source and target files
        setSourceFile(null);
        setTargetFile(null);
        setSourceText('');
        setTargetText('');
        setSourcePreview('');
        setTargetPreview('');
        setSelectedContractId('');
        addToast("Comparison cleared", "info");
        setReviewingEventId(null); // Clear review mode
        setRejectedAdditions(new Set());
        setRejectedRemovals(new Set());
    };

    const handleApplyRename = (rename, index) => {
        const isApplied = appliedRenames.has(index);

        setModifiedTargetData(prevData => {
            const updatedData = JSON.parse(JSON.stringify(prevData || result?.target_data || {}));

            if (isApplied) {
                // Unapply: revert new_field back to old_field
                const revertRenameToObject = (obj) => {
                    if (rename.type === 'edi_segment_change' || rename.type === 'edi_element_change') {
                        if (rename.type === 'edi_segment_change') {
                            const segIdx = rename.segment_num - 1;
                            if (obj.segments && obj.segments[segIdx] && obj.segments[segIdx].segment_id === rename.new_field) {
                                obj.segments[segIdx].segment_id = rename.old_field;
                            } else if (Array.isArray(obj) && obj[segIdx] && obj[segIdx].segment_id === rename.new_field) {
                                obj[segIdx].segment_id = rename.old_field;
                            }
                        }
                        return; // early return so we don't do key rename logic
                    }

                    if (Array.isArray(obj)) {
                        obj.forEach(item => revertRenameToObject(item));
                    } else if (obj && typeof obj === 'object') {
                        if (obj.hasOwnProperty(rename.new_field)) {
                            obj[rename.old_field] = obj[rename.new_field];
                            delete obj[rename.new_field];
                        }
                        Object.values(obj).forEach(value => revertRenameToObject(value));
                    }
                };
                revertRenameToObject(updatedData);
            } else {
                // Apply: rename old_field to new_field
                const applyRenameToObject = (obj) => {
                    if (rename.type === 'edi_segment_change' || rename.type === 'edi_element_change') {
                        if (rename.type === 'edi_segment_change') {
                            const segIdx = rename.segment_num - 1;
                            if (obj.segments && obj.segments[segIdx] && obj.segments[segIdx].segment_id === rename.old_field) {
                                obj.segments[segIdx].segment_id = rename.new_field;
                            } else if (Array.isArray(obj) && obj[segIdx] && obj[segIdx].segment_id === rename.old_field) {
                                obj[segIdx].segment_id = rename.new_field;
                            }
                        }
                        return;
                    }

                    if (Array.isArray(obj)) {
                        obj.forEach(item => applyRenameToObject(item));
                    } else if (obj && typeof obj === 'object') {
                        if (obj.hasOwnProperty(rename.old_field)) {
                            obj[rename.new_field] = obj[rename.old_field];
                            delete obj[rename.old_field];
                        }
                        Object.values(obj).forEach(value => applyRenameToObject(value));
                    }
                };
                applyRenameToObject(updatedData);
            }
            return updatedData;
        });

        if (isApplied) {
            setAppliedRenames(prev => {
                const newSet = new Set(prev);
                newSet.delete(index);
                return newSet;
            });
            addToast(`Reverted rename: ${rename.new_field} → ${rename.old_field}`, "info");
        } else {
            setAppliedRenames(prev => new Set([...prev, index]));
            addToast(`Applied rename: ${rename.old_field} → ${rename.new_field}`, "success");
        }
    };

    const handleApplyRenameBulk = (indices) => {
        if (!indices || indices.length === 0) return;

        // We use functional state update for modifiedTargetData to ensure we don't clobber
        setModifiedTargetData(prevData => {
            const updatedData = JSON.parse(JSON.stringify(prevData || result?.target_data || {}));

            indices.forEach(index => {
                const rename = result.diff.suggested_renames[index];
                if (appliedRenames.has(index)) return; // Don't re-apply

                const applyRenameToObject = (obj) => {
                    if (rename.type === 'edi_segment_change' || rename.type === 'edi_element_change') {
                        if (rename.type === 'edi_segment_change') {
                            const segIdx = rename.segment_num - 1;
                            if (obj.segments && obj.segments[segIdx] && obj.segments[segIdx].segment_id === rename.old_field) {
                                obj.segments[segIdx].segment_id = rename.new_field;
                            } else if (Array.isArray(obj) && obj[segIdx] && obj[segIdx].segment_id === rename.old_field) {
                                obj[segIdx].segment_id = rename.new_field;
                            }
                        }
                        return;
                    }

                    if (Array.isArray(obj)) {
                        obj.forEach(item => applyRenameToObject(item));
                    } else if (obj && typeof obj === 'object') {
                        if (obj.hasOwnProperty(rename.old_field)) {
                            obj[rename.new_field] = obj[rename.old_field];
                            delete obj[rename.old_field];
                        }
                        Object.values(obj).forEach(value => applyRenameToObject(value));
                    }
                };
                applyRenameToObject(updatedData);
            });
            return updatedData;
        });

        setAppliedRenames(prev => new Set([...prev, ...indices]));

        const isAll = indices.length === result?.diff?.suggested_renames?.length;
        const label = isAll ? "all renames" : `renames for "${result.diff.suggested_renames[indices[0]].old_field}"`;
        addToast(`Applied ${indices.length} ${label}`, "success");
    };

    const handleApplyAllRenames = () => {
        if (!result?.diff?.suggested_renames) return;
        const unappliedIndices = result.diff.suggested_renames
            .map((_, i) => i)
            .filter(i => !appliedRenames.has(i));

        if (unappliedIndices.length === 0) {
            addToast("All renames already applied", "info");
            return;
        }

        handleApplyRenameBulk(unappliedIndices);
    };

    const handleAcceptAllAndSave = () => {
        if (!result?.diff?.suggested_renames) return;

        setModifiedTargetData(prevData => {
            let updatedData = JSON.parse(JSON.stringify(prevData || result?.target_data || {}));
            result.diff.suggested_renames.forEach((rename, index) => {
                if (!appliedRenames.has(index)) {
                    const applyRenameToObject = (obj) => {
                        if (rename.type === 'edi_segment_change' || rename.type === 'edi_element_change') {
                            if (rename.type === 'edi_segment_change') {
                                const segIdx = rename.segment_num - 1;
                                if (obj.segments && obj.segments[segIdx] && obj.segments[segIdx].segment_id === rename.old_field) {
                                    obj.segments[segIdx].segment_id = rename.new_field;
                                } else if (Array.isArray(obj) && obj[segIdx] && obj[segIdx].segment_id === rename.old_field) {
                                    obj[segIdx].segment_id = rename.new_field;
                                }
                            }
                            return;
                        }

                        if (Array.isArray(obj)) {
                            obj.forEach(item => applyRenameToObject(item));
                        } else if (obj && typeof obj === 'object') {
                            if (obj.hasOwnProperty(rename.old_field)) {
                                obj[rename.new_field] = obj[rename.old_field];
                                delete obj[rename.old_field];
                            }
                            Object.values(obj).forEach(value => applyRenameToObject(value));
                        }
                    };
                    applyRenameToObject(updatedData);
                }
            });
            return updatedData;
        });

        setAppliedRenames(new Set(result.diff.suggested_renames.map((_, i) => i)));
        openSaveModal('saveTarget');
    };

    // --- REJECT ADDITION: Remove the newly-added field from modifiedTargetData ---
    const handleToggleAddition = (addition, index) => {
        const path = addition.path;
        const isRejected = rejectedAdditions.has(index);

        setModifiedTargetData(prevData => {
            const updatedData = JSON.parse(JSON.stringify(prevData || result?.target_data || {}));
            let obj = updatedData;

            if (isRejected) {
                // Un-reject: re-insert the field back into the target
                for (let i = 0; i < path.length - 1; i++) {
                    if (obj && typeof obj === 'object') obj = obj[path[i]];
                    else break;
                }
                if (obj && typeof obj === 'object') {
                    const key = path[path.length - 1];
                    obj[key] = addition.value;
                }
            } else {
                // Reject: delete the field from the target
                for (let i = 0; i < path.length - 1; i++) {
                    if (obj && typeof obj === 'object') obj = obj[path[i]];
                    else break;
                }
                if (obj && typeof obj === 'object') {
                    const key = path[path.length - 1];
                    if (Array.isArray(obj) && typeof key === 'number') {
                        // verify the value before splicing to avoid index shifting problems
                        // JSON stringify handles deep comparison simply enough for this case
                        if (JSON.stringify(obj[key]) === JSON.stringify(addition.value)) {
                            obj.splice(key, 1);
                        } else {
                            // If index shifted, we could try to find it, but for now simple fallback
                            // Just finding the exact matching object and removing it
                            const actualIndex = obj.findIndex(item => JSON.stringify(item) === JSON.stringify(addition.value));
                            if (actualIndex !== -1) obj.splice(actualIndex, 1);
                        }
                    } else {
                        delete obj[key];
                    }
                }
            }
            return updatedData;
        });

        if (isRejected) {
            setRejectedAdditions(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
            addToast(`Restored addition: ${path[path.length - 1]}`, "info");
        } else {
            setRejectedAdditions(prev => new Set([...prev, index]));
            addToast(`Rejected addition: ${path[path.length - 1]}`, "success");
        }
    };

    // --- REJECT REMOVAL: Re-insert a field that was removed from the target ---
    const handleToggleRemoval = (removal, index) => {
        const path = removal.path;
        const isRejected = rejectedRemovals.has(index);

        setModifiedTargetData(prevData => {
            const updatedData = JSON.parse(JSON.stringify(prevData || result?.target_data || {}));
            let obj = updatedData;

            if (isRejected) {
                // Un-reject: remove the re-inserted field again
                for (let i = 0; i < path.length - 1; i++) {
                    if (obj && typeof obj === 'object') obj = obj[path[i]];
                    else break;
                }
                if (obj && typeof obj === 'object') {
                    const key = path[path.length - 1];
                    if (Array.isArray(obj) && typeof key === 'number') {
                        if (JSON.stringify(obj[key]) === JSON.stringify(removal.value)) {
                            obj.splice(key, 1);
                        } else {
                            const actualIndex = obj.findIndex(item => JSON.stringify(item) === JSON.stringify(removal.value));
                            if (actualIndex !== -1) obj.splice(actualIndex, 1);
                        }
                    } else {
                        delete obj[key];
                    }
                }
            } else {
                // Reject the removal: forcefully re-insert the value from source
                for (let i = 0; i < path.length - 1; i++) {
                    if (obj && typeof obj === 'object') {
                        // Create intermediate objects if they don't exist
                        if (obj[path[i]] === undefined) {
                            obj[path[i]] = typeof path[i + 1] === 'number' ? [] : {};
                        }
                        obj = obj[path[i]];
                    } else break;
                }
                if (obj && typeof obj === 'object') {
                    const key = path[path.length - 1];
                    // If it's an array, we don't 'push' randomly, we insert at the index
                    // However, because of index shifting, this is best-effort
                    if (Array.isArray(obj) && typeof key === 'number') {
                        // ensure the array is long enough (pad with nulls if needed to avoid out of bounds)
                        while (obj.length < key) obj.push(null);
                        obj.splice(key, 0, removal.value);
                    } else {
                        obj[key] = removal.value; // value comes from the source contract
                    }
                }
            }
            return updatedData;
        });

        if (isRejected) {
            setRejectedRemovals(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
            addToast(`Undid removal restore: ${path[path.length - 1]}`, "info");
        } else {
            setRejectedRemovals(prev => new Set([...prev, index]));
            addToast(`Restored removed field: ${path[path.length - 1]}`, "success");
        }
    };

    // --- TOGGLE TYPE CHANGE: Revert the type back to the contract value ---
    const handleToggleTypeChange = (typeChange, index) => {
        const path = typeChange.path;
        const isAccepted = acceptedTypeChanges.has(index);

        setModifiedTargetData(prevData => {
            const updatedData = JSON.parse(JSON.stringify(prevData || result?.target_data || {}));
            let obj = updatedData;

            if (isAccepted) {
                // Un-accept: revert to contract's original value
                if (typeChange.old_value !== undefined && !String(typeChange.old_value).startsWith('<')) {
                    for (let i = 0; i < path.length - 1; i++) {
                        if (obj && typeof obj === 'object') obj = obj[path[i]];
                        else break;
                    }
                    if (obj && typeof obj === 'object') {
                        const key = path[path.length - 1];
                        obj[key] = typeChange.old_value;
                    }
                }
            } else {
                // Accept: keep the new type (restore new_value if it was previously reverted)
                if (typeChange.new_value !== undefined && !String(typeChange.new_value).startsWith('<')) {
                    for (let i = 0; i < path.length - 1; i++) {
                        if (obj && typeof obj === 'object') obj = obj[path[i]];
                        else break;
                    }
                    if (obj && typeof obj === 'object') {
                        const key = path[path.length - 1];
                        obj[key] = typeChange.new_value;
                    }
                }
            }
            return updatedData;
        });

        if (isAccepted) {
            setAcceptedTypeChanges(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
            addToast(`Reverted type change on ${path[path.length - 1]}`, "info");
        } else {
            setAcceptedTypeChanges(prev => new Set([...prev, index]));
            addToast(`Accepted type change on ${path[path.length - 1]}`, "success");
        }
    };


    const handleNavigateToField = (rename) => {
        console.log('[handleNavigateToField] Called with:', rename);
        console.log('[handleNavigateToField] Has segment_id:', rename.segment_id, 'Has segment_num:', rename.segment_num);

        // Special handling for EDI element changes
        if (rename.segment_id && rename.segment_num) {
            const targetId = `edi-segment-${rename.segment_id}-${rename.segment_num}`;
            console.log('[handleNavigateToField] EDI mode - looking for ID:', targetId);

            // Try to scroll to element
            const el = document.getElementById(targetId);
            console.log('[handleNavigateToField] Element found:', !!el, el);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                console.log('[handleNavigateToField] Scrolled to element');
            } else {
                console.warn(`[handleNavigateToField] Element with ID ${targetId} not found in DOM`);
                // Debug: list all edi-segment IDs in DOM
                const allEdiIds = Array.from(document.querySelectorAll('[id^="edi-segment-"]')).map(e => e.id);
                console.log('[handleNavigateToField] Available EDI IDs in DOM:', allEdiIds.slice(0, 10));
            }

            // Set highlight
            setHighlightedField(targetId);
            setTimeout(() => setHighlightedField(null), 2000);
            return;
        }

        console.log('[handleNavigateToField] Non-EDI mode - using old_field:', rename.old_field);

        // Highlight field in text diff
        setHighlightedField(rename.old_field);
        // Clear highlight after 3 seconds
        setTimeout(() => setHighlightedField(null), 3000);
    };





    const handleScrollToTop = () => {
        if (resultsContainerRef.current) {
            resultsContainerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    // Monitor scroll position for back-to-top button
    useEffect(() => {
        const handleScroll = () => {
            if (resultsContainerRef.current) {
                const scrollTop = resultsContainerRef.current.scrollTop;
                setShowBackToTop(scrollTop > 200);
            }
        };

        const container = resultsContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [result]);

    const startResizing = (e) => {
        if (isFullscreen) return;
        isResizing.current = true;
        setIsDragging(true);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', stopResizing);
        document.body.style.userSelect = 'none';
    };

    const handleMouseMove = (e) => {
        if (!isResizing.current) return;
        const newHeight = window.innerHeight - e.clientY;
        // Allow up to 90% of window height
        if (newHeight > 100 && newHeight < window.innerHeight * 0.9) {
            setResultsHeight(newHeight);
        }
    };

    const stopResizing = () => {
        isResizing.current = false;
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', stopResizing);
        document.body.style.userSelect = '';
    };

    return (
        <div className="flex flex-col h-full relative">
            {/* Review Mode Banner */}
            {reviewingEventId && (
                <div className="w-full bg-blue-50 dark:bg-blue-900/40 border-b border-blue-100 dark:border-blue-800/30 px-4 py-1.5 flex items-center justify-center relative z-20 shrink-0">
                    <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        {reviewDriftEvent?.source_name} <span className="text-white mx-1.5 font-normal">→</span> {reviewDriftEvent?.target_name}
                    </div>
                    <button
                        onClick={() => {
                            setReviewingEventId(null);
                            onReviewComplete?.();
                            addToast('Exited review mode', 'info');
                        }}
                        className="absolute right-3 p-1.5 hover:bg-blue-100 dark:hover:bg-blue-800/50 rounded-full text-blue-500 dark:text-blue-300 transition-colors"
                        title="Exit Review Mode"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}
            <Modal
                isOpen={activeModal === 'saveSource' || activeModal === 'saveTarget'}
                onClose={() => setActiveModal(null)}
                title={activeModal === 'saveSource' ? "Save Source as Contract" : (reviewingEventId ? "Save & Resolve Drift" : "Save Changes")}
                footer={
                    <>
                        <button onClick={() => setActiveModal(null)} className="px-4 py-2 text-gray-600 dark:text-[#cccccc] hover:bg-gray-100 dark:hover:bg-[#3c3c3c] rounded-lg">Cancel</button>
                        <button onClick={handleSaveContract} className={`px-4 py-2 text-white rounded-lg ${reviewingEventId ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                            {reviewingEventId
                                ? (saveOption === 'new_contract' ? 'Create & Resolve' : 'Save & Resolve')
                                : (saveOption === 'new_contract' ? 'Create Contract' : 'Save Version')}
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    {/* Review mode banner inside modal */}
                    {reviewingEventId && (
                        <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30 rounded-lg mb-4">
                            <GitMerge className="text-emerald-500" size={18} />
                            <div className="text-sm">
                                <span className="font-medium text-emerald-800 dark:text-emerald-200">Drift Review Mode</span>
                                <span className="text-emerald-600 dark:text-emerald-400 ml-2">— Saving will auto-resolve the drift event</span>
                            </div>
                        </div>
                    )}

                    {/* Toggle between Version and New Contract - hide toggle when reviewing drift with a contract selected */}
                    {contracts.length > 0 && !(reviewingEventId && selectedContractId) && (
                        <div className="flex bg-gray-100 dark:bg-[#3c3c3c] p-1 rounded-lg mb-4">
                            <button
                                onClick={() => setSaveOption('new_version')}
                                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${saveOption === 'new_version'
                                    ? 'bg-white dark:bg-[#4c4c4c] shadow text-blue-600 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                            >
                                {selectedContractId
                                    ? `Update ${contracts.find(c => c.id == selectedContractId)?.name || 'Contract'}`
                                    : 'Update Existing Contract'}
                            </button>
                            <button
                                onClick={() => setSaveOption('new_contract')}
                                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${saveOption === 'new_contract'
                                    ? 'bg-white dark:bg-[#4c4c4c] shadow text-blue-600 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                            >
                                Save as New Contract
                            </button>
                        </div>
                    )}

                    {saveOption === 'new_contract' ? (
                        <>
                            <div className="flex items-center gap-2">
                                <p className="text-sm text-gray-600 dark:text-[#cccccc]">
                                    Save the current schema as a completely new data contract.
                                </p>
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[#cccccc] mb-1">
                                    Contract Name
                                </label>
                                <input
                                    type="text"
                                    value={contractName}
                                    onChange={(e) => setContractName(e.target.value)}
                                    placeholder="e.g., Company 850 v2"
                                    className="w-full p-2 border border-gray-300 dark:border-[#3c3c3c] rounded-lg bg-white dark:bg-[#252526] text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none placeholder-gray-400 dark:placeholder-gray-400"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[#cccccc] mb-1">
                                    Vendor / Group
                                </label>
                                <VendorSelect
                                    value={contractVendor}
                                    onChange={setContractVendor}
                                    autoFillLast={true}
                                />
                                <p className="text-xs text-gray-500 dark:text-[#808080] mt-1">
                                    Contracts with the same vendor will be grouped together
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Contract selector when no contract is pre-selected */}
                            {(!selectedContractId && !selectedContractForVersion) && (
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[#cccccc] mb-1">
                                        Select Contract to Update
                                    </label>
                                    <select
                                        value={selectedContractForVersion}
                                        onChange={(e) => setSelectedContractForVersion(e.target.value)}
                                        className="w-full p-2 border border-gray-300 dark:border-[#3c3c3c] rounded-lg bg-white dark:bg-[#252526] text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">-- Select a contract --</option>
                                        {contracts.map(c => (
                                            <option key={c.id} value={c.id}>{c.name} (v{c.version || 1})</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Contract selector when user wants to change from pre-selected */}
                            {(selectedContractId || selectedContractForVersion) && (
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[#cccccc] mb-1">
                                        Contract to Update
                                    </label>
                                    <select
                                        value={selectedContractForVersion || selectedContractId}
                                        onChange={(e) => setSelectedContractForVersion(e.target.value)}
                                        className="w-full p-2 border border-gray-300 dark:border-[#3c3c3c] rounded-lg bg-white dark:bg-[#252526] text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        {contracts.map(c => (
                                            <option key={c.id} value={c.id}>{c.name} (v{c.version || 1})</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <textarea
                                    value={changeSummary}
                                    onChange={(e) => setChangeSummary(e.target.value)}
                                    placeholder="Change summary (optional)..."
                                    rows={3}
                                    className="w-full p-2 border border-gray-300 dark:border-[#3c3c3c] rounded-lg bg-white dark:bg-[#252526] text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none placeholder-gray-400 dark:placeholder-gray-400 resize-none"
                                    autoFocus
                                />
                            </div>

                            {/* Save Preview - show what's changing */}
                            {result?.diff && (result.diff.suggested_renames?.length > 0 || result.diff.type_changes?.length > 0 || result.diff.added?.length > 0 || result.diff.removed?.length > 0) && (
                                <div className="border border-gray-200 dark:border-[#3c3c3c] rounded-lg overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setSavePreviewOpen(!savePreviewOpen)}
                                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-[#1e1e1e] hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors"
                                    >
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                            <Eye size={14} />
                                            Changes Preview
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                ({(result.diff.suggested_renames?.length || 0) + (result.diff.type_changes?.length || 0) + (result.diff.added?.length || 0) + (result.diff.removed?.length || 0)} changes)
                                            </span>
                                        </span>
                                        {savePreviewOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>

                                    {savePreviewOpen && (
                                        <div className="px-3 py-2 space-y-2 max-h-48 overflow-y-auto bg-white dark:bg-[#252526] text-xs">
                                            {/* Renames */}
                                            {result.diff.suggested_renames?.map((rename, i) => (
                                                <div key={`r-${i}`} className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                                                    <span className={`w-5 h-5 rounded flex items-center justify-center text-xs ${appliedRenames.has(i) ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-gray-100 dark:bg-gray-700'}`}>
                                                        {appliedRenames.has(i) ? <Check size={12} /> : '~'}
                                                    </span>
                                                    <span className="line-through opacity-50">{rename.old_field}</span>
                                                    <ArrowRight size={12} />
                                                    <span className="font-medium">{rename.new_field}</span>
                                                </div>
                                            ))}

                                            {/* Type changes */}
                                            {result.diff.type_changes?.map((tc, i) => (
                                                <div key={`t-${i}`} className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                                                    <span className={`w-5 h-5 rounded flex items-center justify-center text-xs ${acceptedTypeChanges.has(i) ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
                                                        {acceptedTypeChanges.has(i) ? <Check size={12} /> : '⇄'}
                                                    </span>
                                                    <span className="font-mono">{tc.field}</span>
                                                    <span className="opacity-50">{tc.old_type}</span>
                                                    <ArrowRight size={12} />
                                                    <span className="font-medium">{tc.new_type}</span>
                                                </div>
                                            ))}

                                            {/* Added fields */}
                                            {result.diff.added?.slice(0, 5).map((field, i) => (
                                                <div key={`a-${i}`} className="flex items-center gap-2 text-green-700 dark:text-green-400">
                                                    <span className="w-5 h-5 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-xs">+</span>
                                                    <span className="font-mono">{field}</span>
                                                </div>
                                            ))}
                                            {result.diff.added?.length > 5 && (
                                                <div className="text-gray-500 pl-7">...and {result.diff.added.length - 5} more additions</div>
                                            )}

                                            {/* Removed fields */}
                                            {result.diff.removed?.slice(0, 5).map((field, i) => (
                                                <div key={`d-${i}`} className="flex items-center gap-2 text-red-700 dark:text-red-400">
                                                    <span className="w-5 h-5 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-xs">−</span>
                                                    <span className="font-mono line-through">{field}</span>
                                                </div>
                                            ))}
                                            {result.diff.removed?.length > 5 && (
                                                <div className="text-gray-500 pl-7">...and {result.diff.removed.length - 5} more removals</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Modal>

            <Modal
                isOpen={activeModal === 'revert'}
                onClose={() => setActiveModal(null)}
                title="Clear Results?"
                footer={
                    <>
                        <button onClick={() => setActiveModal(null)} className="px-4 py-2 text-gray-600 dark:text-[#cccccc] hover:bg-gray-100 dark:hover:bg-[#3c3c3c] rounded-lg">Cancel</button>
                        <button onClick={handleRevert} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Clear All</button>
                    </>
                }
            >
                <p className="text-gray-600 dark:text-[#cccccc]">Are you sure you want to clear all comparison results and uploaded files?</p>
            </Modal>



            {/* Reject Drift Modal */}
            <Modal
                isOpen={showRejectModal}
                onClose={() => { setShowRejectModal(false); setRejectionReason(''); }}
                title="Reject Drift"
                footer={
                    <>
                        <button onClick={() => { setShowRejectModal(false); setRejectionReason(''); }} className="px-4 py-2 text-[#808080] hover:bg-[#3c3c3c] rounded-lg">Cancel</button>
                        <button
                            onClick={async () => {
                                try {
                                    await rejectDriftEvent(reviewingEventId, {
                                        rejection_reason: rejectionReason || 'Rejected from Comparator',
                                        notify_slack: true
                                    });
                                    addToast('Drift rejected — baseline unchanged', 'success');
                                    setShowRejectModal(false);
                                    setRejectionReason('');
                                    setReviewingEventId(null);
                                    setResult(null);
                                    onReviewComplete?.();
                                } catch (err) {
                                    console.error('Reject drift failed:', err);
                                    addToast('Failed to reject: ' + (err.message || 'Unknown error'), 'error');
                                }
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            Reject & Close
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-red-900/10 border border-red-800/20 rounded-lg">
                        <ShieldX className="text-red-400 flex-shrink-0" size={18} />
                        <p className="text-sm text-red-200">
                            This will close the drift event <strong>without changing the baseline contract</strong>.
                        </p>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-[#cccccc] mb-1.5 block">Reason (optional)</label>
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="e.g., Partner misspelled field names..."
                            rows={2}
                            className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#1e1e1e] text-white focus:ring-2 focus:ring-red-500 outline-none placeholder-gray-500 resize-none text-sm"
                            autoFocus
                        />
                    </div>
                </div>
            </Modal>

            <ExportConfigModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                result={result}
                appliedRenames={appliedRenames}
                addToast={addToast}
            />



            {!isFullscreen && (
                <div className="bg-white dark:bg-[#252526] border-y border-gray-200 dark:border-[#3c3c3c] px-4 py-3 flex justify-between items-center shadow-sm z-10">
                    <div className="flex items-center gap-4">
                        {/* Mode Toggle */}
                        <div className="flex bg-gray-200 dark:bg-[#3c3c3c] rounded-lg p-1">
                            <button
                                onClick={() => setAppMode('compare')}
                                className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${appMode === 'compare'
                                    ? 'bg-white dark:bg-[#4c4c4c] shadow text-blue-600 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                            >
                                <span className="flex items-center gap-1.5">
                                    <ArrowRightLeft size={14} />
                                    <span className="hidden sm:inline">Compare</span>
                                </span>
                            </button>
                            <button
                                onClick={() => setAppMode('view')}
                                className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${appMode === 'view'
                                    ? 'bg-white dark:bg-[#4c4c4c] shadow text-blue-600 dark:text-blue-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                            >
                                <span className="flex items-center gap-1.5">
                                    <Eye size={14} />
                                    <span className="hidden sm:inline">View</span>
                                </span>
                            </button>
                        </div>
                        <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 hidden sm:block" />
                        <h2 className="text-lg font-semibold text-gray-700 dark:text-[#cccccc] hidden sm:block">
                            {appMode === 'compare' ? 'Comparator' : 'File Viewer'}
                        </h2>
                    </div>

                    {appMode === 'compare' && (
                        <div className="flex items-center gap-3">
                            {(sourceFile || sourceText || selectedContractId) && (!targetFile && !targetText) && (
                                <span className="text-sm text-gray-500 dark:text-gray-400">Add target file</span>
                            )}
                            {(!sourceFile && !sourceText && !selectedContractId) && (targetFile || targetText) && (
                                <span className="text-sm text-gray-500 dark:text-gray-400">Add source file</span>
                            )}
                            <button
                                id="run-compare-btn"
                                onClick={handleCompare}
                                disabled={loading || ((!sourceFile && !sourceText && !selectedContractId) || (!targetFile && !targetText))}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? <RefreshCw size={18} className="animate-spin" /> : <Play size={18} />}
                                {loading ? "Comparing..." : "Run Comparison"}
                            </button>
                        </div>
                    )}

                    {appMode === 'view' && (
                        <div className="flex items-center gap-3">
                            <button
                                id="run-view-btn"
                                onClick={handleViewFile}
                                disabled={loading || (!sourceFile && !sourceText)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? <RefreshCw size={18} className="animate-spin" /> : <Eye size={18} />}
                                {loading ? "Parsing..." : "View File"}
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div
                className={`flex-1 flex flex-col md:flex-row overflow-hidden pb-[400px] transition-all duration-300 ${isFullscreen ? 'hidden' : ''}`}
                style={{ paddingBottom: result ? `${resultsHeight}px` : '0px' }}
            >
                <div className={`flex-1 flex flex-col min-w-0 ${appMode === 'compare' ? 'border-b md:border-b-0 md:border-r border-gray-200 dark:border-[#3c3c3c]' : ''} bg-white dark:bg-[#1e1e1e]`}>
                    <div className="p-3 bg-gray-50 dark:bg-[#252526] border-b border-gray-200 dark:border-[#3c3c3c] flex flex-nowrap justify-between items-center gap-2">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="font-semibold text-gray-700 dark:text-[#cccccc] truncate">{appMode === 'compare' ? 'Source' : 'File'}</span>
                        </div>
                        <div className="flex bg-gray-200 dark:bg-[#3c3c3c] rounded-lg p-1">
                            <button onClick={() => setSourceMode('file')} className={`p-1.5 rounded ${sourceMode === 'file' ? 'bg-white dark:bg-[#4c4c4c] shadow' : ''}`} title="File"><Upload size={16} /></button>
                            <button onClick={() => setSourceMode('text')} className={`p-1.5 rounded ${sourceMode === 'text' ? 'bg-white dark:bg-[#4c4c4c] shadow' : ''}`} title="Text"><FileText size={16} /></button>
                            {appMode === 'compare' && isAuthenticated && (
                                <button onClick={() => setSourceMode('contract')} className={`p-1.5 rounded ${sourceMode === 'contract' ? 'bg-white dark:bg-[#4c4c4c] shadow' : ''}`} title="Contract"><Database size={16} /></button>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 p-4 overflow-y-auto relative bg-slate-50 dark:bg-[#1e1e1e]/50">
                        {sourceMode === 'file' && (
                            sourceFile ? (
                                <div className="h-full flex flex-col">
                                    <div className="flex flex-nowrap justify-between items-center mb-2 gap-2">
                                        <span className="text-sm font-medium flex items-center gap-2 min-w-0">
                                            <FileText size={16} className="text-blue-500 flex-shrink-0" />
                                            <span className="truncate">{sourceFile.name}</span>
                                        </span>
                                        <button
                                            onClick={() => { setSourceFile(null); setSourcePreview(''); }}
                                            className="flex items-center gap-1 px-2 py-1 text-xs bg-transparent border border-gray-300 dark:border-[#4c4c4c] hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 rounded transition-colors"
                                        >
                                            <X size={12} />
                                            Remove
                                        </button>
                                    </div>
                                    <div className="flex-1 bg-gray-50 dark:bg-[#252526] border border-gray-200 dark:border-[#3c3c3c] rounded-lg p-3 overflow-auto font-mono text-xs text-gray-600 dark:text-[#cccccc] whitespace-pre">
                                        {sourcePreview || "Loading preview..."}
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={`h-full flex flex-col items-center justify-center border-2 border-dashed rounded-lg transition-colors relative ${isDraggingSource ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20' : 'border-gray-300 dark:border-[#3c3c3c] hover:bg-gray-50 dark:hover:bg-[#252526]/60'}`}
                                    onDragOver={handleDragOverSource}
                                    onDragLeave={handleDragLeaveSource}
                                    onDrop={handleDropSource}
                                >
                                    <input type="file" accept=".json,.xml,.xsd,.edi,.txt,.csv,.x12" onChange={handleSourceFileChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                                    {isDraggingSource ? (
                                        <>
                                            <Upload size={36} className="text-blue-500 mb-3" />
                                            <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Drop to load source</p>
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={30} className="text-gray-400 dark:text-[#808080] mb-3" />
                                            <p className="text-sm font-medium text-gray-700 dark:text-[#cccccc] mb-1">
                                                {appMode === 'compare' ? 'Source file' : 'Select a file'}
                                            </p>
                                            <p className="text-xs text-gray-400 dark:text-slate-500 mb-4">Drag and drop, or click to browse</p>
                                            <p className="text-[10px] text-gray-400 dark:text-slate-500">JSON, OpenAPI, X12 EDI, XML</p>
                                            <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-5">
                                                The original version, used as a baseline
                                            </p>
                                            {!isAuthenticated && appMode === 'compare' && (
                                                <div className="absolute bottom-3 left-3 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                                    <button
                                                        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); loadSample(EDI_SAMPLE); }}
                                                        className="px-2 py-1 text-[10px] text-[#808080] hover:text-[#cccccc] border border-[#3c3c3c] hover:border-[#555] rounded transition-colors bg-[#1e1e1e]"
                                                    >
                                                        Try EDI sample
                                                    </button>
                                                    <button
                                                        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); loadSample(JSON_SAMPLE); }}
                                                        className="px-2 py-1 text-[10px] text-[#808080] hover:text-[#cccccc] border border-[#3c3c3c] hover:border-[#555] rounded transition-colors bg-[#1e1e1e]"
                                                    >
                                                        Try JSON sample
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )
                        )}
                        {sourceMode === 'text' && (
                            <textarea
                                className="w-full h-full p-2 border border-gray-300 dark:border-[#3c3c3c] rounded-lg bg-gray-50 dark:bg-[#252526] font-mono text-xs resize-none"
                                placeholder="Paste JSON or X12 content..."
                                value={sourceText}
                                onChange={(e) => setSourceText(e.target.value)}
                            />
                        )}
                        {sourceMode === 'contract' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-[#cccccc] block mb-2">Select Contract</label>
                                    <select
                                        className="w-full p-2 border border-gray-300 dark:border-[#3c3c3c] rounded-lg bg-white dark:bg-[#252526]"
                                        value={selectedContractId}
                                        onChange={(e) => setSelectedContractId(e.target.value)}
                                    >
                                        <option value="">-- Select --</option>
                                        {contracts.map(c => (
                                            <option key={c.id} value={c.id}>{c.name} (v{c.version || 1})</option>
                                        ))}
                                    </select>
                                </div>
                                {selectedContractId && (
                                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                                        <p className="text-sm text-blue-800 dark:text-blue-200">Contract Selected</p>
                                        <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">Ready to compare against target.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {appMode === 'compare' && (
                    <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-[#1e1e1e]">
                        <div className="p-3 bg-gray-50 dark:bg-[#252526] border-b border-gray-200 dark:border-[#3c3c3c] flex flex-nowrap justify-between items-center gap-2">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="font-semibold text-gray-700 dark:text-[#cccccc] truncate">Target</span>
                            </div>
                            <div className="flex bg-gray-200 dark:bg-[#3c3c3c] rounded-lg p-1">
                                <button onClick={() => setTargetMode('file')} className={`p-1.5 rounded ${targetMode === 'file' ? 'bg-white dark:bg-[#4c4c4c] shadow' : ''}`} title="File"><Upload size={16} /></button>
                                <button onClick={() => setTargetMode('text')} className={`p-1.5 rounded ${targetMode === 'text' ? 'bg-white dark:bg-[#4c4c4c] shadow' : ''}`} title="Text"><FileText size={16} /></button>
                            </div>
                        </div>

                        <div className="flex-1 p-4 overflow-y-auto relative bg-slate-50 dark:bg-[#1e1e1e]/50">
                            {targetMode === 'file' && (
                                targetFile ? (
                                    <div className="h-full flex flex-col">
                                        <div className="flex flex-nowrap justify-between items-center mb-2 gap-2">
                                            <span className="text-sm font-medium flex items-center gap-2 min-w-0">
                                                <FileText size={16} className="text-green-500 flex-shrink-0" />
                                                <span className="truncate">{targetFile.name}</span>
                                            </span>
                                            <button
                                                onClick={() => { setTargetFile(null); setTargetPreview(''); }}
                                                className="flex items-center gap-1 px-2 py-1 text-xs bg-transparent border border-gray-300 dark:border-[#4c4c4c] hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 rounded transition-colors"
                                            >
                                                <X size={12} />
                                                Remove
                                            </button>
                                        </div>
                                        <div className="flex-1 bg-gray-50 dark:bg-[#252526] border border-gray-200 dark:border-[#3c3c3c] rounded-lg p-3 overflow-auto font-mono text-xs text-gray-600 dark:text-[#cccccc] whitespace-pre">
                                            {targetPreview || "Loading preview..."}
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className={`h-full flex flex-col items-center justify-center border-2 border-dashed rounded-lg transition-colors relative ${isDraggingTarget ? 'border-green-500 bg-green-50 dark:border-green-400 dark:bg-green-900/20' : 'border-gray-300 dark:border-[#3c3c3c] hover:bg-gray-50 dark:hover:bg-[#252526]/60'}`}
                                        onDragOver={handleDragOverTarget}
                                        onDragLeave={handleDragLeaveTarget}
                                        onDrop={handleDropTarget}
                                    >
                                        <input type="file" accept=".json,.xml,.xsd,.edi,.txt,.csv,.x12" onChange={handleTargetFileChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                                        {isDraggingTarget ? (
                                            <>
                                                <Upload size={36} className="text-green-500 mb-3" />
                                                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Drop to load target</p>
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={30} className="text-gray-400 dark:text-[#808080] mb-3" />
                                                <p className="text-sm font-medium text-gray-700 dark:text-[#cccccc] mb-1">Target file</p>
                                                <p className="text-xs text-gray-400 dark:text-slate-500 mb-4">Drag and drop, or click to browse</p>
                                                <p className="text-[10px] text-gray-400 dark:text-slate-500">JSON, OpenAPI, X12 EDI, XML</p>
                                                <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-5">The newer version to compare against</p>
                                            </>
                                        )}
                                    </div>
                                )
                            )}
                            {targetMode === 'text' && (
                                <textarea
                                    className="w-full h-full p-2 border border-gray-300 dark:border-[#3c3c3c] rounded-lg bg-gray-50 dark:bg-[#252526] font-mono text-xs resize-none"
                                    placeholder="Paste JSON or X12 content..."
                                    value={targetText}
                                    onChange={(e) => setTargetText(e.target.value)}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {result && (
                <div
                    className={`bg-white dark:bg-[#252526] border-t border-gray-200 dark:border-[#3c3c3c] shadow-[0_-4px_20px_rgba(0,0,0,0.1)] z-20 flex flex-col ${isDragging ? '' : 'transition-all duration-300'} ${isFullscreen ? 'absolute inset-0 h-full' : 'absolute bottom-0 left-0 right-0'}`}
                    style={{ height: isFullscreen ? '100%' : `${resultsHeight}px` }}
                >
                    {!isFullscreen && (
                        <div
                            className="h-3 bg-gray-100 dark:bg-[#3c3c3c] hover:bg-blue-100 dark:hover:bg-blue-900 cursor-ns-resize flex justify-center items-center border-b border-gray-200 dark:border-[#4c4c4c] transition-colors"
                            onMouseDown={startResizing}
                        >
                            <GripHorizontal size={16} className="text-gray-400 dark:text-[#808080]" />
                        </div>
                    )}

                    <div className="px-4 py-2 border-b border-gray-100 dark:border-[#3c3c3c] flex flex-wrap items-center justify-between gap-3 bg-gray-50 dark:bg-[#252526]">
                        {/* Left: Search Bar */}
                        <div className="flex-shrink-0 flex items-center gap-2">
                            <SmartSearchBar
                                ref={searchInputRef}
                                searchQuery={searchQuery}
                                onSearchChange={setSearchQuery}
                                searchType={searchType}
                                onSearchTypeChange={setSearchType}
                                currentMatch={currentMatchIndex}
                                totalMatches={searchMatches.length}
                                onPrevMatch={() => setCurrentMatchIndex(Math.max(1, currentMatchIndex - 1))}
                                onNextMatch={() => setCurrentMatchIndex(Math.min(searchMatches.length, currentMatchIndex + 1))}
                                changesFilter={changesFilter}
                                onChangesFilterChange={setChangesFilter}
                                showSchemaDriftOnly={showSchemaDriftOnly}
                                onShowSchemaDriftOnlyChange={setShowSchemaDriftOnly}
                                isCompareMode={appMode === 'compare'}
                                isViewMode={appMode === 'view'}
                            />
                        </div>

                        {/* Right: Controls (View + Actions) */}
                        <div className="flex items-center gap-2 justify-end flex-grow">

                            {/* Fullscreen */}
                            <button
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors border shadow-sm ${isFullscreen
                                    ? 'bg-gray-100 dark:bg-[#4c4c4c] text-gray-900 dark:text-gray-100 border-gray-400 dark:border-gray-500'
                                    : 'bg-white dark:bg-[#3c3c3c] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-[#4c4c4c] hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                            >
                                {isFullscreen ? <Minimize2 size={16} /> : <Expand size={16} />}
                            </button>

                            <div className="w-px h-6 bg-gray-300 dark:bg-[#4c4c4c] mx-0.5"></div>

                            {appMode === 'compare' && (
                                <button
                                    onClick={() => handleCompare()}
                                    className="flex items-center gap-2 px-2 sm:px-3 py-1.5 text-xs bg-white dark:bg-[#3c3c3c] border border-gray-300 dark:border-[#4c4c4c] hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                                    title="Re-run comparison with source inputs"
                                >
                                    <RefreshCw size={14} />
                                    <span className="hidden lg:inline">Re-compare</span>
                                </button>
                            )}

                            <button
                                onClick={() => setActiveModal('revert')}
                                className="flex items-center gap-2 px-2 sm:px-3 py-1.5 text-xs bg-white dark:bg-[#3c3c3c] border border-gray-300 dark:border-[#4c4c4c] hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                            >
                                <X size={14} />
                                <span className="hidden lg:inline">Clear</span>
                            </button>
                            {appMode === 'compare' && reviewingEventId && (
                                <>
                                    <button
                                        onClick={() => openSaveModal('saveTarget')}
                                        className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-emerald-400 hover:bg-emerald-900/40"
                                    >
                                        <GitMerge size={13} />
                                        <span className="hidden lg:inline">Save & Resolve</span>
                                    </button>
                                    <button
                                        onClick={() => setShowRejectModal(true)}
                                        className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-red-400 hover:bg-red-900/40"
                                    >
                                        <ShieldX size={13} />
                                        <span className="hidden lg:inline">Reject</span>
                                    </button>
                                </>
                            )}
                            {appMode === 'compare' && !reviewingEventId && isAuthenticated && (
                                <button
                                    onClick={() => openSaveModal('saveTarget')}
                                    className="flex items-center gap-2 px-2 sm:px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
                                >
                                    <Save size={14} />
                                    <span className="hidden lg:inline">
                                        {appliedRenames.size > 0 && result?.diff?.suggested_renames?.length > 0
                                            ? `Save (${appliedRenames.size}/${result.diff.suggested_renames.length})`
                                            : 'Save'}
                                    </span>
                                </button>
                            )}
                            <ExportDropdown
                                result={result}
                                source={result?.source_data}
                                target={result?.target_data}
                                sourceName={selectedContractId ? contracts.find(c => c.id === parseInt(selectedContractId))?.name || 'Source' : sourceFile?.name?.replace(/\.[^/.]+$/, '') || 'source'}
                                targetName={targetFile?.name?.replace(/\.[^/.]+$/, '') || 'target'}
                                sourceFile={sourceFile}
                                targetFile={targetFile}
                                appliedRenames={appliedRenames}
                                diffLines={result?.diff?.actions || []}
                                addToast={addToast}
                                defaultFormat={matchingSettings.defaultExportFormat}
                                onExportConfig={() => setShowExportModal(true)}
                                appMode={appMode}
                            />
                        </div>
                    </div>


                    <div ref={resultsContainerRef} className={`flex-1 bg-gray-50 dark:bg-[#1e1e1e] ${isFullscreen ? 'overflow-hidden flex flex-col' : 'overflow-y-auto p-3'} relative`}>
                        <div className={`${isFullscreen ? 'w-full h-full flex flex-col' : 'mx-auto space-y-3'}`}>
                            {/* Upsell banner — only for unauthenticated users, dismissable per session */}
                            {!isAuthenticated && !upsellDismissed && (
                                <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[#1a2a3a] border border-blue-900/50 text-sm">
                                    <div className="flex items-center gap-2 text-blue-300 min-w-0">
                                        <span className="truncate text-xs">
                                            Keep a versioned record of your schemas and get alerted when partners change their data formats.
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            onClick={onSignInClick}
                                            className="px-3 py-1 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                                        >
                                            Sign in
                                        </button>
                                        <button
                                            onClick={dismissUpsell}
                                            className="text-[#555] hover:text-[#999] transition-colors p-0.5"
                                            aria-label="Dismiss"
                                        >
                                            <X size={13} />
                                        </button>
                                    </div>
                                </div>
                            )}
                            {/* Summary Banner */}


                            {(() => {
                                const _noise = new Set(['schema_constraint', 'schema_set_item']);
                                const hasRenames = result.diff?.suggested_renames?.length > 0;
                                const hasTypeChanges = result.diff?.type_changes?.length > 0;
                                const hasAdditions = (result.diff?.added || []).some(a => !_noise.has(a.semantic_type));
                                const hasRemovals = (result.diff?.removed || []).some(r => !_noise.has(r.semantic_type));
                                return hasRenames || hasTypeChanges || hasAdditions || hasRemovals;
                            })() && (
                                    <div className={`bg-gray-100 dark:bg-[#2a2a2a] border border-gray-200 dark:border-[#3a3a3a] shadow-sm ${isFullscreen ? 'p-2 m-2 rounded-lg flex-shrink-0' : 'p-3 rounded-xl'}`}>
                                        <div className={`flex flex-wrap items-center ${isRenamesCollapsed ? '' : 'gap-4 mb-2'}`}>
                                            <button
                                                onClick={() => setIsRenamesCollapsed(!isRenamesCollapsed)}
                                                className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 font-bold hover:text-gray-900 dark:hover:text-gray-100"
                                            >
                                                Drift Resolution
                                                {isRenamesCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                                            </button>

                                            {!isRenamesCollapsed && (
                                                <>
                                                    <div className="flex flex-wrap items-center gap-3 text-xs">
                                                        {/* Summary stats with confidence zones */}
                                                        {result.diff?.suggested_renames?.length > 0 && (
                                                            <>
                                                                {(() => {
                                                                    const total = result.diff?.suggested_renames?.length || 0;
                                                                    const applied = appliedRenames.size;
                                                                    const highConf = result.diff?.suggested_renames?.filter(r => Math.round(r.confidence * 100) >= matchingSettings.highConfidenceThreshold).length || 0;
                                                                    const medConf = result.diff?.suggested_renames?.filter(r => {
                                                                        const pct = Math.round(r.confidence * 100);
                                                                        return pct >= matchingSettings.greyThreshold && pct < matchingSettings.highConfidenceThreshold;
                                                                    }).length;
                                                                    const uncertain = total - highConf - medConf;

                                                                    return (
                                                                        <>
                                                                            <span className="font-medium text-gray-700 dark:text-[#cccccc]">
                                                                                {applied}/{total} renames
                                                                            </span>
                                                                            <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                                                                                {highConf} high
                                                                            </span>
                                                                            <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">
                                                                                {medConf} med
                                                                            </span>
                                                                            <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-[#3c3c3c] text-gray-500 dark:text-[#808080] rounded">
                                                                                {uncertain} uncertain
                                                                            </span>

                                                                            {applied < total && (
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleApplyAllRenames();
                                                                                    }}
                                                                                    className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold bg-gray-100 text-gray-600 hover:bg-blue-600 hover:text-white dark:bg-[#3c3c3c] dark:text-gray-300 dark:hover:bg-blue-600 dark:hover:text-white rounded-md transition-all ml-1"
                                                                                >
                                                                                    <Check size={10} />
                                                                                    Apply All
                                                                                </button>
                                                                            )}

                                                                        </>
                                                                    );
                                                                })()}
                                                            </>
                                                        )}
                                                        {result.diff?.type_changes?.length > 0 && (
                                                            <span className="text-yellow-600 dark:text-yellow-400 font-medium border-l border-gray-300 dark:border-[#4c4c4c] pl-2">
                                                                {acceptedTypeChanges.size}/{result.diff?.type_changes?.length || 0} types
                                                            </span>
                                                        )}
                                                        {(() => {
                                                            const _noise = new Set(['schema_constraint', 'schema_set_item']);
                                                            const _renameNew = new Set((result.diff?.suggested_renames || []).map(r => r.new_field));
                                                            const _renameOld = new Set((result.diff?.suggested_renames || []).map(r => r.old_field));
                                                            const addCount = (result.diff?.added || []).filter(a => {
                                                                if (_noise.has(a.semantic_type)) return false;
                                                                const f = Array.isArray(a.path) ? a.path[a.path.length - 1] : a.path;
                                                                return !_renameNew.has(f);
                                                            }).length;
                                                            const remCount = (result.diff?.removed || []).filter(r => {
                                                                if (_noise.has(r.semantic_type)) return false;
                                                                const f = Array.isArray(r.path) ? r.path[r.path.length - 1] : r.path;
                                                                return !_renameOld.has(f);
                                                            }).length;
                                                            return (<>
                                                                {addCount > 0 && (
                                                                    <span className="text-emerald-600 dark:text-emerald-400 font-medium border-l border-gray-300 dark:border-[#4c4c4c] pl-2">
                                                                        {rejectedAdditions.size}/{addCount} additions
                                                                    </span>
                                                                )}
                                                                {remCount > 0 && (
                                                                    <span className="text-red-600 dark:text-red-400 font-medium border-l border-gray-300 dark:border-[#4c4c4c] pl-2">
                                                                        {rejectedRemovals.size}/{remCount} removals
                                                                    </span>
                                                                )}
                                                            </>);
                                                        })()}
                                                    </div>
                                                    <div className="h-px bg-gray-300 dark:bg-[#444] flex-1"></div>
                                                </>
                                            )}
                                        </div>
                                        {!isRenamesCollapsed && (
                                            <div className={`grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${isFullscreen ? 'max-h-[180px] overflow-y-auto pr-1' : ''}`}>
                                                {/* Unified List Logic with Confidence Groups */}
                                                {(() => {
                                                    // 1. Group renames by old_field
                                                    const groupedRenames = new Map();
                                                    (result.diff?.suggested_renames || []).forEach((r, i) => {
                                                        if (!groupedRenames.has(r.old_field)) {
                                                            groupedRenames.set(r.old_field, []);
                                                        }
                                                        groupedRenames.get(r.old_field).push({
                                                            ...r,
                                                            originalIndex: i,
                                                            confidencePct: Math.round(r.confidence * 100)
                                                        });
                                                    });

                                                    const renameGroups = Array.from(groupedRenames.entries()).map(([oldField, candidates]) => {
                                                        // Sort candidates by confidence
                                                        candidates.sort((a, b) => b.confidencePct - a.confidencePct);
                                                        return {
                                                            kind: 'rename_group',
                                                            id: `group-${oldField}`,
                                                            oldField,
                                                            candidates,
                                                            sortScore: candidates[0].confidencePct // Sort group by best candidate
                                                        };
                                                    });

                                                    // 2. Prepare type changes
                                                    const typeChanges = (result.diff?.type_changes || []).map((t, i) => {
                                                        let score = 80;
                                                        if (t.severity === 'high') score = 100;
                                                        if (t.severity === 'medium') score = 90;
                                                        return {
                                                            ...t,
                                                            id: `type-${i}`,
                                                            originalIndex: i,
                                                            kind: 'type_change',
                                                            sortScore: score,
                                                            // Extract simple name for label
                                                            label: (Array.isArray(t.path) ? t.path.join('.') : t.path).split('.').pop()
                                                        };
                                                    });

                                                    // 3. Prepare additions — exclude schema noise and rename pair members
                                                    const SCHEMA_NOISE = new Set(['schema_constraint', 'schema_set_item']);
                                                    const renameNewFields = new Set((result.diff?.suggested_renames || []).map(r => r.new_field));
                                                    const renameOldFields = new Set((result.diff?.suggested_renames || []).map(r => r.old_field));
                                                    const additions = (result.diff?.added || [])
                                                        .filter(a => {
                                                            if (SCHEMA_NOISE.has(a.semantic_type)) return false;
                                                            const fieldName = (Array.isArray(a.path) ? a.path[a.path.length - 1] : a.path);
                                                            return !renameNewFields.has(fieldName);
                                                        })
                                                        .map((a, i) => {
                                                            let label;
                                                            if (a.label) {
                                                                label = a.label;
                                                            } else if (Array.isArray(a.path) && a.path[0] === 'po1_loop') {
                                                                label = `PO1 Line Item ${(a.path[1] ?? 0) + 1}`;
                                                            } else {
                                                                label = (Array.isArray(a.path) ? a.path.join('.') : a.path).split('.').pop();
                                                            }
                                                            // For EDI: compute highlight ID to scroll diff viewer to the right segment
                                                            let ediHighlightId = null;
                                                            if (Array.isArray(a.path)) {
                                                                if (a.path[0] === 'segments' && typeof a.path[1] === 'number') {
                                                                    const seg = a.value;
                                                                    if (seg?.segment_id && seg?.segment_num) {
                                                                        ediHighlightId = `edi-segment-${seg.segment_id}-${seg.segment_num}`;
                                                                    }
                                                                } else if (a.path[0] === 'po1_loop' && typeof a.path[1] === 'number') {
                                                                    const tgtSegs = result?.target_data?.segments || [];
                                                                    const po1Segs = tgtSegs.filter(s => s.segment_id === 'PO1');
                                                                    const match = po1Segs[a.path[1]];
                                                                    if (match?.segment_num) ediHighlightId = `edi-segment-PO1-${match.segment_num}`;
                                                                }
                                                            }
                                                            return { ...a, id: `add-${i}`, originalIndex: i, kind: 'addition', sortScore: 70, label, ediHighlightId };
                                                        });

                                                    // 4. Prepare removals — exclude schema noise and rename pair members
                                                    const removals = (result.diff?.removed || [])
                                                        .filter(r => {
                                                            if (SCHEMA_NOISE.has(r.semantic_type)) return false;
                                                            const fieldName = (Array.isArray(r.path) ? r.path[r.path.length - 1] : r.path);
                                                            return !renameOldFields.has(fieldName);
                                                        })
                                                        .map((r, i) => {
                                                            let label;
                                                            if (r.label) {
                                                                label = r.label;
                                                            } else if (Array.isArray(r.path) && r.path[0] === 'po1_loop') {
                                                                label = `PO1 Line Item ${(r.path[1] ?? 0) + 1}`;
                                                            } else {
                                                                label = (Array.isArray(r.path) ? r.path.join('.') : r.path).split('.').pop();
                                                            }
                                                            let ediHighlightId = null;
                                                            if (Array.isArray(r.path)) {
                                                                if (r.path[0] === 'segments' && typeof r.path[1] === 'number') {
                                                                    const seg = r.value;
                                                                    if (seg?.segment_id && seg?.segment_num) {
                                                                        ediHighlightId = `edi-segment-${seg.segment_id}-${seg.segment_num}`;
                                                                    }
                                                                } else if (r.path[0] === 'po1_loop' && typeof r.path[1] === 'number') {
                                                                    const srcSegs = result?.source_data?.segments || [];
                                                                    const po1Segs = srcSegs.filter(s => s.segment_id === 'PO1');
                                                                    const match = po1Segs[r.path[1]];
                                                                    if (match?.segment_num) ediHighlightId = `edi-segment-PO1-${match.segment_num}`;
                                                                }
                                                            }
                                                            return { ...r, id: `rem-${i}`, originalIndex: i, kind: 'removal', sortScore: 95, label, ediHighlightId };
                                                        });

                                                    // 5. Render cards for all change types
                                                    const allItems = [
                                                        ...renameGroups,
                                                        ...typeChanges,
                                                        ...additions,
                                                        ...removals,
                                                    ].sort((a, b) => b.sortScore - a.sortScore);

                                                    return allItems.map((item) => {

                                                        // --- RENDER ADDITION ---
                                                        if (item.kind === 'addition') {
                                                            const isRejected = rejectedAdditions.has(item.originalIndex);
                                                            const valType = item.value !== null && item.value !== undefined ? (Array.isArray(item.value) ? 'array' : typeof item.value) : null;
                                                            return (
                                                                <div key={item.id} className={`bg-white dark:bg-[#252526] border ${isRejected ? 'border-gray-200 dark:border-[#4c4c4c]' : 'border-green-200 dark:border-green-800'} rounded-xl shadow-sm transition-all duration-200 hover:shadow-md flex flex-col overflow-hidden w-full`}>
                                                                    <div className="px-3 py-2 border-b border-gray-100 dark:border-[#3c3c3c]/50 bg-gray-50/50 dark:bg-[#2a2a2b] flex items-center justify-between">
                                                                        <span className={`text-sm font-mono font-bold ${isRejected ? 'text-gray-400 line-through' : 'text-green-700 dark:text-green-400'}`}>
                                                                            {item.label}
                                                                        </span>
                                                                    </div>
                                                                    <div className="divide-y divide-gray-100 dark:divide-[#3c3c3c]/30">
                                                                        <div className="px-3 py-2 flex items-center justify-between gap-2 group hover:bg-gray-50 dark:hover:bg-[#2a2a2b] transition-colors">
                                                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                                <Plus size={12} className="text-green-400 dark:text-green-600 flex-shrink-0" />
                                                                                {valType && valType !== 'object' && <span className="text-[10px] font-bold text-green-600 dark:text-green-500 flex-shrink-0">{valType}</span>}
                                                                            </div>
                                                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                                                <button onClick={() => { const hid = item.ediHighlightId || item.label; if (item.ediHighlightId) { const el = document.getElementById(item.ediHighlightId); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } setHighlightedField(hid); setTimeout(() => setHighlightedField(null), 2000); }} className="p-1.5 text-gray-400 hover:text-blue-500 rounded" title="View in diff"><Eye size={14} /></button>
                                                                                <button onClick={() => handleToggleAddition(item, item.originalIndex)} className={`ml-1 px-2 py-1 rounded-md text-[10px] font-bold transition-all ${isRejected ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#3c3c3c] dark:text-gray-300 dark:hover:bg-[#4c4c4c]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#3c3c3c] dark:text-gray-300 dark:hover:bg-[#4c4c4c]'}`}>{isRejected ? 'Restore' : 'Ignore'}</button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        // --- RENDER REMOVAL ---
                                                        if (item.kind === 'removal') {
                                                            const isRestored = rejectedRemovals.has(item.originalIndex);
                                                            const valType = item.value !== null && item.value !== undefined ? (Array.isArray(item.value) ? 'array' : typeof item.value) : null;
                                                            return (
                                                                <div key={item.id} className={`${isRestored ? 'bg-green-50 dark:bg-green-900/10' : 'bg-white dark:bg-[#252526]'} border ${isRestored ? 'border-green-300 dark:border-green-700' : 'border-gray-200 dark:border-[#4c4c4c]'} rounded-xl shadow-sm transition-all duration-200 hover:shadow-md flex flex-col overflow-hidden w-full`}>
                                                                    <div className="px-3 py-2 border-b border-gray-100 dark:border-[#3c3c3c]/50 bg-gray-50/50 dark:bg-[#2a2a2b] flex items-center justify-between">
                                                                        <span className={`text-sm font-mono font-bold ${isRestored ? 'text-gray-400' : 'text-red-700 dark:text-red-400'}`}>
                                                                            {item.label}
                                                                        </span>
                                                                    </div>
                                                                    <div className="divide-y divide-gray-100 dark:divide-[#3c3c3c]/30">
                                                                        <div className="px-3 py-2 flex items-center justify-between gap-2 group hover:bg-gray-50 dark:hover:bg-[#2a2a2b] transition-colors">
                                                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                                <Minus size={12} className="text-red-400 dark:text-red-600 flex-shrink-0" />
                                                                                {valType && valType !== 'object' && <span className="text-[10px] font-bold text-red-500 dark:text-red-400 flex-shrink-0">{valType}</span>}
                                                                            </div>
                                                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                                                <button onClick={() => { const hid = item.ediHighlightId || item.label; if (item.ediHighlightId) { const el = document.getElementById(item.ediHighlightId); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } setHighlightedField(hid); setTimeout(() => setHighlightedField(null), 2000); }} className="p-1.5 text-gray-400 hover:text-blue-500 rounded" title="View in diff"><Eye size={14} /></button>
                                                                                <button onClick={() => handleToggleRemoval(item, item.originalIndex)} className={`ml-1 px-2 py-1 rounded-md text-[10px] font-bold transition-all ${isRestored ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#3c3c3c] dark:text-gray-300 dark:hover:bg-[#4c4c4c]'}`}>{isRestored ? 'Restored' : 'Restore'}</button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        // --- RENDER TYPE CHANGE ---
                                                        if (item.kind === 'type_change') {
                                                            const isAccepted = acceptedTypeChanges.has(item.originalIndex);
                                                            const isHigh = item.severity === 'high';
                                                            return (
                                                                <div key={item.id} className={`${isAccepted ? 'bg-green-50 dark:bg-green-900/10' : 'bg-white dark:bg-[#252526]'} border ${isAccepted ? 'border-green-300 dark:border-green-700' : isHigh ? 'border-orange-200 dark:border-orange-800' : 'border-gray-200 dark:border-[#4c4c4c]'} rounded-xl shadow-sm transition-all duration-200 hover:shadow-md flex flex-col overflow-hidden w-full`}>
                                                                    <div className="px-3 py-2 border-b border-gray-100 dark:border-[#3c3c3c]/50 bg-gray-50/50 dark:bg-[#2a2a2b] flex items-center justify-between">
                                                                        <span className={`text-sm font-mono font-bold ${isAccepted ? 'text-gray-400' : isHigh ? 'text-orange-700 dark:text-orange-400' : 'text-yellow-700 dark:text-yellow-400'}`}>
                                                                            {item.label}
                                                                        </span>
                                                                        {isHigh && !isAccepted && <AlertTriangle size={11} className="text-orange-500 flex-shrink-0" />}
                                                                    </div>
                                                                    <div className="divide-y divide-gray-100 dark:divide-[#3c3c3c]/30">
                                                                        <div className="px-3 py-2 flex flex-col gap-1 group hover:bg-gray-50 dark:hover:bg-[#2a2a2b] transition-colors">
                                                                            <div className="flex items-center justify-between gap-2">
                                                                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                                    <ArrowRight size={12} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                                                                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400 line-through">{item.old_type}</span>
                                                                                    <ArrowRight size={10} className="text-gray-400 flex-shrink-0" />
                                                                                    <span className={`text-xs font-mono font-bold ${isAccepted ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>{item.new_type}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                                                    <button onClick={() => { setHighlightedField(item.label); setTimeout(() => setHighlightedField(null), 2000); }} className="p-1.5 text-gray-400 hover:text-blue-500 rounded" title="View in diff"><Eye size={14} /></button>
                                                                                    <button onClick={() => handleToggleTypeChange(item, item.originalIndex)} className={`ml-1 px-2 py-1 rounded-md text-[10px] font-bold transition-all flex items-center gap-1 ${isAccepted ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#3c3c3c] dark:text-gray-300 dark:hover:bg-[#4c4c4c]'}`}>{isAccepted ? <><Check size={10} />Accepted</> : 'Accept'}</button>
                                                                                </div>
                                                                            </div>
                                                                            {isHigh && !isAccepted && <span className="text-[10px] text-orange-700/80 dark:text-orange-400/80 italic font-medium ml-4">Breaking type change</span>}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        // --- RENDER RENAME GROUP ---
                                                        // Filter out low confidence candidates based on settings
                                                        const visibleCandidates = item.candidates.filter(c => c.confidencePct >= matchingSettings.hideThreshold);
                                                        if (visibleCandidates.length === 0) return null;

                                                        // Check if any candidate in this group is applied
                                                        const appliedCandidateIndex = visibleCandidates.findIndex(c => appliedRenames.has(c.originalIndex));
                                                        const isAnyApplied = appliedCandidateIndex !== -1;
                                                        const appliedCandidate = isAnyApplied ? visibleCandidates[appliedCandidateIndex] : null;

                                                        // Determine group style based on best candidate or applied state
                                                        const bestConfidence = isAnyApplied ? appliedCandidate.confidencePct : visibleCandidates[0].confidencePct;
                                                        let groupZone = 'uncertain';
                                                        if (bestConfidence >= matchingSettings.highConfidenceThreshold) groupZone = 'high';
                                                        else if (bestConfidence >= matchingSettings.greyThreshold) groupZone = 'medium';

                                                        const borderColor = isAnyApplied
                                                            ? 'border-green-300 dark:border-green-700'
                                                            : groupZone === 'high' ? 'border-green-200 dark:border-green-800'
                                                                : groupZone === 'medium' ? 'border-orange-200 dark:border-orange-800'
                                                                    : 'border-gray-200 dark:border-[#4c4c4c]';

                                                        const bgColor = isAnyApplied
                                                            ? 'bg-green-50 dark:bg-green-900/10'
                                                            : 'bg-white dark:bg-[#252526]';

                                                        return (
                                                            <div
                                                                key={item.id}
                                                                className={`${bgColor} border ${borderColor} rounded-xl shadow-sm transition-all duration-200 hover:shadow-md flex flex-col overflow-hidden w-full`}
                                                            >
                                                                {/* Header: Old Field */}
                                                                <div className="px-3 py-2 border-b border-gray-100 dark:border-[#3c3c3c]/50 bg-gray-50/50 dark:bg-[#2a2a2b] flex items-center justify-between">
                                                                    <span className={`text-sm font-mono font-bold ${isAnyApplied ? 'text-gray-400 line-through' : 'text-orange-700 dark:text-orange-400'}`}>
                                                                        {item.oldField}
                                                                    </span>
                                                                </div>

                                                                {/* Candidates List */}
                                                                <div className="divide-y divide-gray-100 dark:divide-[#3c3c3c]/30">
                                                                    {(() => {
                                                                        // Deduplicate identical candidates
                                                                        const uniqueCandidates = [];
                                                                        const seenKeys = new Set();
                                                                        visibleCandidates.forEach(c => {
                                                                            const key = `${c.new_field}-${c.old_value}-${c.new_value}-${c.note}`;
                                                                            if (!seenKeys.has(key)) {
                                                                                seenKeys.add(key);
                                                                                uniqueCandidates.push({
                                                                                    ...c,
                                                                                    occurrences: [c.originalIndex]
                                                                                });
                                                                            } else {
                                                                                const existing = uniqueCandidates.find(xc => `${xc.new_field}-${xc.old_value}-${xc.new_value}-${xc.note}` === key);
                                                                                if (existing) existing.occurrences.push(c.originalIndex);
                                                                            }
                                                                        });

                                                                        return uniqueCandidates.map((candidate) => {
                                                                            const isThisApplied = candidate.occurrences.every(idx => appliedRenames.has(idx));

                                                                            // Confidence Pill Style
                                                                            let confColor = 'text-gray-400 dark:text-gray-500';
                                                                            if (candidate.confidencePct >= matchingSettings.highConfidenceThreshold) {
                                                                                confColor = 'text-green-600 dark:text-green-500';
                                                                            } else if (candidate.confidencePct >= matchingSettings.greyThreshold) {
                                                                                confColor = 'text-orange-500 dark:text-orange-400';
                                                                            }

                                                                            // Navigate button
                                                                            const handleNav = (e) => {
                                                                                e.stopPropagation();
                                                                                handleNavigateToField({ old_field: item.oldField, ...candidate });
                                                                            };

                                                                            return (
                                                                                <div
                                                                                    key={`cand-${candidate.originalIndex}`}
                                                                                    className={`px-3 py-2 flex flex-col group hover:bg-gray-50 dark:hover:bg-[#2a2a2b] transition-colors ${isThisApplied ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`}
                                                                                >
                                                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                                            <ArrowRight size={12} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                                                                            <span className={`text-sm font-bold truncate ${isThisApplied ? 'text-gray-700 dark:text-gray-400 line-through opacity-70' : 'text-gray-700 dark:text-gray-200'}`}>
                                                                                                {item.oldField === candidate.new_field ? (
                                                                                                    <span className="flex items-center gap-1.5 font-mono text-xs">
                                                                                                        <span className="text-gray-400 dark:text-gray-500">{String(candidate.old_value || '')}</span>
                                                                                                        <ArrowRight size={10} className="text-gray-300" />
                                                                                                        <span className="text-orange-600 dark:text-orange-400">{String(candidate.new_value || '')}</span>
                                                                                                    </span>
                                                                                                ) : (
                                                                                                    candidate.new_field
                                                                                                )}
                                                                                            </span>

                                                                                            {/* Occurrences Badge */}
                                                                                            {candidate.occurrences.length > 1 && (
                                                                                                <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
                                                                                                    {candidate.occurrences.length}x
                                                                                                </span>
                                                                                            )}

                                                                                            {/* Confidence Badge */}
                                                                                            <span className={`ml-1 text-[10px] font-bold ${confColor}`}>
                                                                                                {candidate.confidencePct}%
                                                                                            </span>
                                                                                        </div>

                                                                                        <div className="flex items-center gap-1 transition-opacity self-end sm:self-auto">
                                                                                            <button
                                                                                                onClick={handleNav}
                                                                                                className="p-1.5 text-gray-400 hover:text-blue-500 rounded"
                                                                                                title="View field"
                                                                                            >
                                                                                                <Eye size={14} />
                                                                                            </button>
                                                                                            <button
                                                                                                onClick={() => {
                                                                                                    if (isThisApplied) {
                                                                                                        // If applied, unapply the first one as unapply doesn't support bulk yet,
                                                                                                        // but usually users don't unapply. If they do, they can click multiple times.
                                                                                                        // Just unapply all of them.
                                                                                                        candidate.occurrences.forEach(idx => handleApplyRename(result.diff.suggested_renames[idx], idx));
                                                                                                    } else {
                                                                                                        handleApplyRenameBulk(candidate.occurrences);
                                                                                                    }
                                                                                                }}
                                                                                                disabled={isAnyApplied && !isThisApplied}
                                                                                                className={`ml-1 px-2 py-1 rounded-md text-[10px] font-bold transition-all flex items-center gap-1
                                                                                        ${isThisApplied
                                                                                                        ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60'
                                                                                                        : isAnyApplied
                                                                                                            ? 'opacity-0'
                                                                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[#3c3c3c] dark:text-gray-300 dark:hover:bg-[#4c4c4c]'
                                                                                                    }
                                                                                    `}
                                                                                            >
                                                                                                {isThisApplied ? 'Applied' : (candidate.occurrences.length > 1 ? `Apply (${candidate.occurrences.length})` : 'Apply')}
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>

                                                                                    {/* Note Display (e.g., Type Mismatch) */}
                                                                                    {candidate.note && (
                                                                                        <div className="mt-1 flex items-start ml-4">
                                                                                            <span className="text-[10px] text-orange-700/80 dark:text-orange-400/80 italic font-medium">
                                                                                                {candidate.note}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                )}



                            <div className={isFullscreen ? 'flex-1 overflow-hidden' : 'h-[600px]'}>
                                <DiffViewer
                                    source={result.source_data}
                                    target={result.target_data}
                                    sourceName={selectedContractId ? contracts.find(c => c.id === parseInt(selectedContractId))?.name || 'Source' : 'Source'}
                                    targetName="Target"
                                    highlightedField={highlightedField}
                                    suggestedRenames={result.diff?.suggested_renames || []}
                                    diffActions={result.diff?.actions || []}
                                    typeChanges={result.diff?.type_changes || []}
                                    cascadingImpacts={result.diff?.cascading_impacts || []}
                                    formattingOnly={result.diff?.formatting_only || []}
                                    diffResult={result.diff || null}
                                    isFullscreen={isFullscreen}
                                    settings={matchingSettings}
                                    isViewMode={appMode === 'view'}
                                    searchQuery={searchQuery}
                                    searchType={searchType}
                                    currentMatchIndex={currentMatchIndex}
                                    changesFilter={changesFilter}
                                    showSchemaDriftOnly={showSchemaDriftOnly}
                                    onSearchMatchesChange={setSearchMatches}
                                    showTypes={matchingSettings.showTypeAnnotations !== false}
                                    onEdiStats={setEdiStats}
                                    elementChanges={result?.diff?.element_changes || EMPTY_ARRAY}
                                    key={`diff-${searchQuery}-${changesFilter}-${showSchemaDriftOnly}`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )
            }

            {/* Keyboard Shortcuts overlay — triggered by pressing '?' */}
            {
                showShortcuts && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowShortcuts(false)} />
                        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-72 bg-[#252526] border border-[#3c3c3c] rounded-xl shadow-2xl p-4 text-xs">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-white font-semibold text-sm">Keyboard Shortcuts</p>
                                <button onClick={() => setShowShortcuts(false)} className="text-[#808080] hover:text-white">✕</button>
                            </div>
                            <div className="space-y-2">
                                {[
                                    { keys: ['Ctrl', 'Enter'], desc: 'Run comparison' },
                                    { keys: ['/'], desc: 'Focus search' },
                                    { keys: ['Esc'], desc: 'Close panel / clear search / fullscreen' },
                                    { keys: ['j', '↓'], desc: 'Next change' },
                                    { keys: ['k', '↑'], desc: 'Previous change' },
                                    { keys: ['?'], desc: 'Toggle this panel' },
                                ].map(({ keys, desc }) => (
                                    <div key={desc} className="flex items-center justify-between gap-2">
                                        <span className="text-[#808080]">{desc}</span>
                                        <div className="flex items-center gap-1 shrink-0">
                                            {keys.map(k => (
                                                <span key={k} className="px-1.5 py-0.5 bg-[#3c3c3c] text-[#cccccc] rounded font-mono text-[10px]">{k}</span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )
            }
        </div >
    );
};

export default Comparator;
